import { 
  Users, 
  DollarSign, 
  CheckSquare, 
  Calendar,
  Target,
  Award,
  UserPlus,
  Repeat,
  TrendingUp,
  Grid3X3,
  BarChart3,
  LineChart,
  UserX,
  Star,
  Activity,
  PieChart,
  Clock,
  Lightbulb,
  CalendarDays,
  Dumbbell,
  CreditCard,
  Package,
  Settings,
  ClipboardCheck,
  MessageSquare,
  Plus,
  AlertTriangle,
  Bell,
  Trophy,
  type LucideIcon
} from "lucide-react";

export type WidgetType = 'kpi' | 'chart' | 'table' | 'activity' | 'shortcut' | 'alert' | 'list';
export type WidgetSize = 'small' | 'medium' | 'large' | 'full';
export type WidgetCategory = 'overview' | 'performance' | 'finance' | 'activity' | 'shortcuts' | 'alerts';
export type DashboardTab = 'overview' | 'performance' | 'finances' | 'activity';

export interface WidgetDefinition {
  id: string;
  nameKey: string;
  descriptionKey: string;
  type: WidgetType;
  defaultSize: WidgetSize;
  icon: LucideIcon;
  category: WidgetCategory;
  tab?: DashboardTab; // Which tab this widget belongs to
  path?: string; // For shortcuts
  refreshInterval?: number; // In seconds, for real-time widgets
  requiresData?: boolean; // If widget needs backend data
}

// KPI Widgets
export const KPI_WIDGETS: WidgetDefinition[] = [
  {
    id: 'kpi-members',
    nameKey: 'widgets.kpiMembers.name',
    descriptionKey: 'widgets.kpiMembers.description',
    type: 'kpi',
    defaultSize: 'small',
    icon: Users,
    category: 'overview',
    requiresData: true,
  },
  {
    id: 'kpi-revenue',
    nameKey: 'widgets.kpiRevenue.name',
    descriptionKey: 'widgets.kpiRevenue.description',
    type: 'kpi',
    defaultSize: 'small',
    icon: DollarSign,
    category: 'finance',
    requiresData: true,
  },
  {
    id: 'kpi-bookings',
    nameKey: 'widgets.kpiBookings.name',
    descriptionKey: 'widgets.kpiBookings.description',
    type: 'kpi',
    defaultSize: 'small',
    icon: CheckSquare,
    category: 'overview',
    requiresData: true,
  },
  {
    id: 'kpi-classes',
    nameKey: 'widgets.kpiClasses.name',
    descriptionKey: 'widgets.kpiClasses.description',
    type: 'kpi',
    defaultSize: 'small',
    icon: Calendar,
    category: 'overview',
    requiresData: true,
  },
  {
    id: 'kpi-occupation',
    nameKey: 'widgets.kpiOccupation.name',
    descriptionKey: 'widgets.kpiOccupation.description',
    type: 'kpi',
    defaultSize: 'small',
    icon: Target,
    category: 'performance',
    requiresData: true,
  },
  {
    id: 'kpi-retention',
    nameKey: 'widgets.kpiRetention.name',
    descriptionKey: 'widgets.kpiRetention.description',
    type: 'kpi',
    defaultSize: 'small',
    icon: Award,
    category: 'performance',
    requiresData: true,
  },
  {
    id: 'kpi-new-members',
    nameKey: 'widgets.kpiNewMembers.name',
    descriptionKey: 'widgets.kpiNewMembers.description',
    type: 'kpi',
    defaultSize: 'small',
    icon: UserPlus,
    category: 'overview',
    requiresData: true,
  },
  {
    id: 'kpi-renewals',
    nameKey: 'widgets.kpiRenewals.name',
    descriptionKey: 'widgets.kpiRenewals.description',
    type: 'kpi',
    defaultSize: 'small',
    icon: Repeat,
    category: 'overview',
    requiresData: true,
  },
];

// Retirés du registre : `kpi-cancellation-rate`, `kpi-active-week`,
// `kpi-expiring-soon`, `kpi-pending-payments` et `kpi-mrr`. Aucune de ces
// cinq tuiles n'avait de source : elles affichaient « 8 % », `totalMembers * 0.7`,
// `5`, `3` et `revenue * 0.8` en dur, sans le signaler. `dashboard-kpis` ne
// calcule ni taux d'annulation, ni actifs sur 7 jours, ni MRR ; les deux
// listes « expirent bientôt » et « paiements en attente » y sont plafonnées à
// 10 lignes sans total, donc inexploitables comme compteur. Elles pourront
// revenir dès que la fonction edge exposera ces valeurs (voir le rapport).
// Un identifiant absent du registre est ignoré par le rendu et par le
// configurateur : les configurations déjà enregistrées restent lisibles.

// Chart Widgets
export const CHART_WIDGETS: WidgetDefinition[] = [
  {
    id: 'chart-members-evolution',
    nameKey: 'widgets.chartMembersEvolution.name',
    descriptionKey: 'widgets.chartMembersEvolution.description',
    type: 'chart',
    defaultSize: 'large',
    icon: TrendingUp,
    category: 'overview',
    requiresData: true,
  },
  {
    id: 'chart-growth',
    nameKey: 'widgets.chartGrowth.name',
    descriptionKey: 'widgets.chartGrowth.description',
    type: 'chart',
    defaultSize: 'large',
    icon: TrendingUp,
    category: 'overview',
    tab: 'overview',
    requiresData: true,
  },
  {
    id: 'chart-heatmap',
    nameKey: 'widgets.chartHeatmap.name',
    descriptionKey: 'widgets.chartHeatmap.description',
    type: 'chart',
    defaultSize: 'large',
    icon: Grid3X3,
    category: 'performance',
    tab: 'performance',
    requiresData: true,
  },
  {
    id: 'chart-bookings',
    nameKey: 'widgets.chartBookings.name',
    descriptionKey: 'widgets.chartBookings.description',
    type: 'chart',
    defaultSize: 'large',
    icon: BarChart3,
    category: 'performance',
    tab: 'performance',
    requiresData: true,
  },
  {
    id: 'chart-revenue',
    nameKey: 'widgets.chartRevenue.name',
    descriptionKey: 'widgets.chartRevenue.description',
    type: 'chart',
    defaultSize: 'large',
    icon: LineChart,
    category: 'finance',
    tab: 'finances',
    requiresData: true,
  },
  {
    id: 'chart-noshow',
    nameKey: 'widgets.chartNoshow.name',
    descriptionKey: 'widgets.chartNoshow.description',
    type: 'chart',
    defaultSize: 'medium',
    icon: UserX,
    category: 'performance',
    tab: 'performance',
    requiresData: true,
  },
  {
    id: 'chart-coach-ratings',
    nameKey: 'widgets.chartCoachRatings.name',
    descriptionKey: 'widgets.chartCoachRatings.description',
    type: 'chart',
    defaultSize: 'medium',
    icon: Star,
    category: 'performance',
    tab: 'performance',
    requiresData: true,
  },
  {
    id: 'chart-coach-perf',
    nameKey: 'widgets.chartCoachPerf.name',
    descriptionKey: 'widgets.chartCoachPerf.description',
    type: 'chart',
    defaultSize: 'large',
    icon: Activity,
    category: 'performance',
    tab: 'performance',
    requiresData: true,
  },
  {
    id: 'chart-sub-status',
    nameKey: 'widgets.chartSubStatus.name',
    descriptionKey: 'widgets.chartSubStatus.description',
    type: 'chart',
    defaultSize: 'medium',
    icon: PieChart,
    category: 'finance',
    tab: 'finances',
    requiresData: true,
  },
  {
    id: 'chart-revenue-sub',
    nameKey: 'widgets.chartRevenueSub.name',
    descriptionKey: 'widgets.chartRevenueSub.description',
    type: 'chart',
    defaultSize: 'medium',
    icon: PieChart,
    category: 'finance',
    tab: 'finances',
    requiresData: true,
  },
  {
    id: 'chart-payment-methods',
    nameKey: 'widgets.chartPaymentMethods.name',
    descriptionKey: 'widgets.chartPaymentMethods.description',
    type: 'chart',
    defaultSize: 'medium',
    icon: CreditCard,
    category: 'finance',
    tab: 'finances',
    requiresData: true,
  },
  {
    id: 'chart-revenue-duration',
    nameKey: 'widgets.chartRevenueDuration.name',
    descriptionKey: 'widgets.chartRevenueDuration.description',
    type: 'chart',
    defaultSize: 'medium',
    icon: Clock,
    category: 'finance',
    tab: 'finances',
    requiresData: true,
  },
];

// List Widgets
export const LIST_WIDGETS: WidgetDefinition[] = [
  {
    id: 'list-top-courses',
    nameKey: 'widgets.listTopCourses.name',
    descriptionKey: 'widgets.listTopCourses.description',
    type: 'list',
    defaultSize: 'medium',
    icon: Trophy,
    category: 'performance',
    requiresData: true,
  },
  {
    id: 'list-top-coaches',
    nameKey: 'widgets.listTopCoaches.name',
    descriptionKey: 'widgets.listTopCoaches.description',
    type: 'list',
    defaultSize: 'medium',
    icon: Star,
    category: 'performance',
    requiresData: true,
  },
  {
    id: 'list-upcoming-classes',
    nameKey: 'widgets.listUpcomingClasses.name',
    descriptionKey: 'widgets.listUpcomingClasses.description',
    type: 'list',
    defaultSize: 'medium',
    icon: CalendarDays,
    category: 'overview',
    requiresData: true,
  },
  {
    id: 'list-recent-members',
    nameKey: 'widgets.listRecentMembers.name',
    descriptionKey: 'widgets.listRecentMembers.description',
    type: 'list',
    defaultSize: 'medium',
    icon: UserPlus,
    category: 'overview',
    requiresData: true,
  },
];

// Shortcut Widgets
export const SHORTCUT_WIDGETS: WidgetDefinition[] = [
  {
    id: 'shortcut-calendar',
    nameKey: 'widgets.shortcutCalendar.name',
    descriptionKey: 'widgets.shortcutCalendar.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: CalendarDays,
    category: 'shortcuts',
    path: '/admin/schedule',
  },
  {
    id: 'shortcut-clients',
    nameKey: 'widgets.shortcutClients.name',
    descriptionKey: 'widgets.shortcutClients.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: Users,
    category: 'shortcuts',
    path: '/admin/clients',
  },
  {
    id: 'shortcut-coaches',
    nameKey: 'widgets.shortcutCoaches.name',
    descriptionKey: 'widgets.shortcutCoaches.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: Dumbbell,
    category: 'shortcuts',
    path: '/admin/coaches',
  },
  {
    id: 'shortcut-courses',
    nameKey: 'widgets.shortcutCourses.name',
    descriptionKey: 'widgets.shortcutCourses.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: Calendar,
    category: 'shortcuts',
    path: '/admin/courses',
  },
  {
    id: 'shortcut-payments',
    nameKey: 'widgets.shortcutPayments.name',
    descriptionKey: 'widgets.shortcutPayments.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: CreditCard,
    category: 'shortcuts',
    path: '/admin/payments',
  },
  {
    id: 'shortcut-inventory',
    nameKey: 'widgets.shortcutInventory.name',
    descriptionKey: 'widgets.shortcutInventory.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: Package,
    category: 'shortcuts',
    path: '/admin/inventory',
  },
  {
    id: 'shortcut-settings',
    nameKey: 'widgets.shortcutSettings.name',
    descriptionKey: 'widgets.shortcutSettings.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: Settings,
    category: 'shortcuts',
    path: '/admin/settings',
  },
  {
    id: 'shortcut-attendance',
    nameKey: 'widgets.shortcutAttendance.name',
    descriptionKey: 'widgets.shortcutAttendance.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: ClipboardCheck,
    category: 'shortcuts',
    path: '/admin/attendance',
  },
  {
    id: 'shortcut-reviews',
    nameKey: 'widgets.shortcutReviews.name',
    descriptionKey: 'widgets.shortcutReviews.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: MessageSquare,
    category: 'shortcuts',
    path: '/admin/reviews',
  },
  {
    id: 'shortcut-new-client',
    nameKey: 'widgets.shortcutNewClient.name',
    descriptionKey: 'widgets.shortcutNewClient.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: Plus,
    category: 'shortcuts',
    path: '/admin/clients?action=new',
  },
  {
    id: 'shortcut-new-course',
    nameKey: 'widgets.shortcutNewCourse.name',
    descriptionKey: 'widgets.shortcutNewCourse.description',
    type: 'shortcut',
    defaultSize: 'small',
    icon: Plus,
    category: 'shortcuts',
    path: '/admin/schedule?action=new',
  },
];

// Alert Widgets
export const ALERT_WIDGETS: WidgetDefinition[] = [
  {
    id: 'alert-expiring-subs',
    nameKey: 'widgets.alertExpiringSubs.name',
    descriptionKey: 'widgets.alertExpiringSubs.description',
    type: 'alert',
    defaultSize: 'medium',
    icon: AlertTriangle,
    category: 'alerts',
    requiresData: true,
    refreshInterval: 300,
  },
  {
    id: 'alert-pending-payments',
    nameKey: 'widgets.alertPendingPayments.name',
    descriptionKey: 'widgets.alertPendingPayments.description',
    type: 'alert',
    defaultSize: 'medium',
    icon: CreditCard,
    category: 'alerts',
    requiresData: true,
    refreshInterval: 300,
  },
  {
    id: 'alert-low-stock',
    nameKey: 'widgets.alertLowStock.name',
    descriptionKey: 'widgets.alertLowStock.description',
    type: 'alert',
    defaultSize: 'medium',
    icon: Package,
    category: 'alerts',
    requiresData: true,
    refreshInterval: 600,
  },
];

// Activity Widgets
export const ACTIVITY_WIDGETS: WidgetDefinition[] = [
  {
    id: 'activity-recent',
    nameKey: 'widgets.activityRecent.name',
    descriptionKey: 'widgets.activityRecent.description',
    type: 'activity',
    defaultSize: 'full',
    icon: Activity,
    category: 'activity',
    tab: 'activity',
    requiresData: true,
  },
  {
    id: 'activity-insights',
    nameKey: 'widgets.activityInsights.name',
    descriptionKey: 'widgets.activityInsights.description',
    type: 'activity',
    defaultSize: 'full',
    icon: Lightbulb,
    category: 'overview',
    tab: 'overview',
    requiresData: true,
  },
];

// All widgets combined
export const ALL_WIDGETS: WidgetDefinition[] = [
  ...KPI_WIDGETS,
  ...CHART_WIDGETS,
  ...LIST_WIDGETS,
  ...SHORTCUT_WIDGETS,
  ...ALERT_WIDGETS,
  ...ACTIVITY_WIDGETS,
];

// Widget registry map for quick lookup
export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = ALL_WIDGETS.reduce(
  (acc, widget) => ({ ...acc, [widget.id]: widget }),
  {}
);

/**
 * Onglet qui rend réellement un widget.
 *
 * `getWidgetTab` ne sert qu'à décider quels onglets afficher
 * (`hasVisibleWidgetsForTab`). Il doit donc désigner l'onglet qui sait *rendre*
 * le widget, pas celui que suggère son thème.
 *
 * Répartition réelle du rendu :
 * - `PerformanceTab`, `FinancesTab` et `ActivityTab` ne rendent qu'une liste
 *   d'identifiants de graphiques codés en dur — tous porteurs d'un `tab`
 *   explicite dans ce registre ;
 * - `OverviewTab` rend `getVisibleWidgets()`, c'est-à-dire **tous** les autres
 *   (KPI, listes, raccourcis, alertes), sans filtrage par onglet.
 *
 * Un repli par catégorie rattachait donc `kpi-revenue` (`category: 'finance'`,
 * sans `tab`) à l'onglet « Finances », qui ne sait pas le rendre : sur la
 * configuration par défaut — où `kpi-revenue` est visible mais aucun
 * `chart-*` finance ne l'est — l'onglet « Finances » apparaissait **vide**,
 * alors que le KPI s'affichait en fait dans « Vue d'ensemble ». Même piège
 * pour `kpi-occupation`, `kpi-retention`, `list-top-courses` et
 * `list-top-coaches` (`category: 'performance'`, sans `tab`).
 *
 * Le repli correct est donc « Vue d'ensemble », sans condition. Cela préserve
 * l'intention du correctif d'origine (l'onglet « Vue d'ensemble » ne doit pas
 * disparaître alors que ses KPI sont actifs).
 */
export function getWidgetTab(widget: WidgetDefinition): DashboardTab {
  return widget.tab ?? 'overview';
}

// Get widgets by category
export function getWidgetsByCategory(category: WidgetCategory): WidgetDefinition[] {
  return ALL_WIDGETS.filter(w => w.category === category);
}

// Get widgets by type
export function getWidgetsByType(type: WidgetType): WidgetDefinition[] {
  return ALL_WIDGETS.filter(w => w.type === type);
}

// Category label translation keys for UI (namespace: adminui2)
export const CATEGORY_LABEL_KEYS: Record<WidgetCategory, string> = {
  overview: 'categoryLabels.overview',
  performance: 'categoryLabels.performance',
  finance: 'categoryLabels.finance',
  activity: 'categoryLabels.activity',
  shortcuts: 'categoryLabels.shortcuts',
  alerts: 'categoryLabels.alerts',
};

// Type label translation keys for UI (namespace: adminui2)
export const TYPE_LABEL_KEYS: Record<WidgetType, string> = {
  kpi: 'typeLabels.kpi',
  chart: 'typeLabels.chart',
  table: 'typeLabels.table',
  activity: 'typeLabels.activity',
  shortcut: 'typeLabels.shortcut',
  alert: 'typeLabels.alert',
  list: 'typeLabels.list',
};

// Widget size label translation keys for UI (namespace: adminui2)
export const SIZE_LABEL_KEYS: Record<WidgetSize, string> = {
  small: 'sizeLabels.small',
  medium: 'sizeLabels.medium',
  large: 'sizeLabels.large',
  full: 'sizeLabels.full',
};

// Default widget configuration
export const DEFAULT_WIDGET_ORDER: string[] = [
  'shortcut-calendar',
  'shortcut-clients',
  'shortcut-courses',
  'shortcut-payments',
  'alert-expiring-subs',
  'alert-pending-payments',
  'kpi-members',
  'kpi-classes',
  'kpi-bookings',
  'kpi-revenue',
  'kpi-new-members',
  'kpi-renewals',
  'kpi-occupation',
  'kpi-retention',
  'chart-members-evolution',
  'chart-growth',
  'chart-heatmap',
  'list-top-courses',
  'list-upcoming-classes',
  'activity-insights',
];
